// Thin wrappers around the auth-related endpoints. Returns are typed so
// callers can branch on the 2FA-required case without inspecting raw JSON.

import { API_BASE_URL, authedFetch } from "./http";
import type { AuthUser } from "./store";

export class AuthApiError extends Error {
  status: number;
  code: string;
  serverMessage: string | null;
  constructor(code: string, status: number, serverMessage: string | null) {
    super(serverMessage ?? code);
    this.name = "AuthApiError";
    this.status = status;
    this.code = code;
    this.serverMessage = serverMessage;
  }
}

async function parseError(res: Response): Promise<AuthApiError> {
  let code = `http_${res.status}`;
  let message: string | null = null;
  try {
    const body = (await res.json()) as {
      error?: unknown;
      message?: unknown;
    };
    if (typeof body.error === "string") code = body.error;
    if (typeof body.message === "string") message = body.message;
  } catch {
    // body wasn't JSON — fall back to the status code
  }
  return new AuthApiError(code, res.status, message);
}

export type LoginResult =
  | {
      kind: "session";
      accessToken: string;
      expiresIn: number;
      user: AuthUser;
    }
  | { kind: "requires_2fa"; mfaSession: string };

interface LoginResponseSession {
  access_token: string;
  expires_in: number;
  user: AuthUser;
}

interface LoginResponseMfa {
  requires_2fa: true;
  mfa_session: string;
}

export async function loginRequest(input: {
  email: string;
  password: string;
}): Promise<LoginResult> {
  const res = await fetch(`${API_BASE_URL}/auth/login`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await parseError(res);
  const body = (await res.json()) as LoginResponseSession | LoginResponseMfa;
  if ("requires_2fa" in body && body.requires_2fa) {
    return { kind: "requires_2fa", mfaSession: body.mfa_session };
  }
  const session = body as LoginResponseSession;
  return {
    kind: "session",
    accessToken: session.access_token,
    expiresIn: session.expires_in,
    user: session.user,
  };
}

export async function verify2faRequest(input: {
  mfaSession: string;
  code: string;
  trustDevice: boolean;
}): Promise<{ accessToken: string; expiresIn: number; user: AuthUser }> {
  const res = await fetch(`${API_BASE_URL}/auth/2fa/verify`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({
      mfa_session: input.mfaSession,
      code: input.code,
      trust_device: input.trustDevice,
    }),
  });
  if (!res.ok) throw await parseError(res);
  const body = (await res.json()) as {
    access_token: string;
    expires_in: number;
    user: AuthUser;
  };
  return {
    accessToken: body.access_token,
    expiresIn: body.expires_in,
    user: body.user,
  };
}

export async function fetchCurrentUser(): Promise<AuthUser | null> {
  const res = await authedFetch("/auth/me", { skipAuthRetry: false });
  if (res.status === 401) return null;
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as AuthUser;
}
